import { useGlobalStore } from "@/store/GlobalStore";
import styled from "styled-components";
import { HeaderItem } from "@/components/RenderbusLayout";
import { AccountType } from "project-config/constants/user-info";
import { UserInfo } from "@/utils/userinfo/types";
import MainAccountIcon from "@/assets/icons/profile/main-account-icon";
import SubAccountIcon from "@/assets/icons/profile/sub-account-icon";
import { Typography } from "antd";
import { ProfileAuthStatus } from "@/pages/profile/components/profile-info-card/components/profile-auth-status";
import { HelperLine, ProfileCard } from "@/pages/profile/components/atom";
import { useTranslation } from "react-i18next";
import { redirectToAuth } from "@/pages/profile/helper/redirectToAuth";
import { ProfileChargeCard } from "@/pages/profile/components/profile-charge-card";
import { CapacityCard } from "@/pages/profile/components/profile-charge-card/components/charge-small-card/capacity-card";
import { useState } from "react";
import { Tabs } from "task-eva-ui";
import { getTranslateNsT } from "@/utils/getTranslateNsT";
import { DesktopChargeCard } from "../desktop-charge-card";
const { Paragraph } = Typography;

export function ProfileInfoCard() {
  const {
    userInfo,
    isDomestic,
    isStudentValid,
    isSubAccount,
    studentCertifyInfo,
    isPersonal,
    fetchUserDesktopInfo,
  } = useGlobalStore();
  const [activeKey, setActiveKey] = useState("render");
  const t = getTranslateNsT("profile");
  return (
    <ProfileCard className="profile-card--main">
      <div>
        <ProfileInfoWrapper>
          <AvatarIcon>
            {userInfo?.userName?.substring(0, 1).toUpperCase()}
          </AvatarIcon>
          <ProfileMainInfo>
            <ProfileNickname>
              {userInfo?.userName || "Unknown"}
              {userInfo && isDomestic && !isSubAccount && (
                <ProfileAuthStatus
                  isStudent={isStudentValid}
                  isPersonal={isPersonal}
                  status={userInfo?.authStatus}
                  onClick={() =>
                    redirectToAuth(userInfo, studentCertifyInfo?.studentBlock)
                  }
                />
              )}
            </ProfileNickname>
            <UserIdentify userInfo={userInfo} />
          </ProfileMainInfo>
        </ProfileInfoWrapper>
        <Tabs
          activeKey={activeKey}
          className="profile-desktop-tabs"
          onChange={(value) => {
            setActiveKey(value);
            if (value === "desktop") {
              void fetchUserDesktopInfo();
            }
          }}
          items={[
            {
              key: "render",
              label: t("tab_render"),
              children: <ProfileChargeCard />,
            },
            {
              key: "desktop",
              label: t("tab_desktop"),
              hidden: !userInfo?.enableRemoteDesktop,
              children: <DesktopChargeCard />,
            },
          ]}
        />
      </div>
      {activeKey === "render" && <CapacityCard />}
    </ProfileCard>
  );
}

function UserIdentify({ userInfo }: { userInfo?: UserInfo }) {
  const { userName, fatherUuid = "" } = userInfo || {};
  const mainAccount = userInfo?.accountType === AccountType.Main;
  const formattedUid = fatherUuid.replace(/(?!^)(?=(\d{4})+$)/g, " ");

  const { t } = useTranslation("profile");
  return (
    <UserIdentifyWrapper>
      {mainAccount ? <MainAccountIcon /> : <SubAccountIcon />}
      {mainAccount ? t("admin") : t("sub-account")}
      <HelperLine />
      <span className="user-id">
        {mainAccount ? `UUID: ${formattedUid}` : `@${formattedUid}`}
      </span>

      <RenderbusParagraph
        copyable={{
          text: mainAccount ? fatherUuid : `${userName}@${fatherUuid}`,
        }}
      />
    </UserIdentifyWrapper>
  );
}

const RenderbusParagraph = styled(Paragraph)`
  &.ant-typography {
    margin-bottom: 0;
  }
  .ant-typography-copy {
    color: rgba(255, 255, 255, 0.9);
  }
`;

const UserIdentifyWrapper = styled.div`
  display: flex;
  align-items: center;
  gap: 4px;
  color: rgba(255, 255, 255, 0.85);
  line-height: 20px;
  .user-id {
    color: #9ba2b0;
  }
`;

const AvatarIcon = styled(HeaderItem)`
  width: 58px;
  height: 58px;
  font-size: 30px;
  background: linear-gradient(
    98.16deg,
    var(--main-color) 2.09%,
    var(--color-main-gradient) 98.11%
  );
  @media (max-width: 1440px) {
    width: 52px;
    height: 52px;
    font-size: 24px;
  }
`;

const ProfileMainInfo = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: space-between;
  height: 58px;
  margin-top: 0;
  padding: 0 16px;
  @media (max-width: 1440px) {
    padding: 0 18px;
  }
`;

const ProfileNickname = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  font-size: 22px;
  line-height: 31px;
`;

const ProfileInfoWrapper = styled.div`
  display: flex;
  align-items: center;
`;
